import React, { useEffect, useState } from "react";
import { api, money, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus as PlusIcon, Minus as MinusIcon, Trash as TrashIcon, ShoppingCart as ShoppingCartIcon, Receipt as ReceiptIcon, Printer as PrinterIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { printInvoice } from "@/lib/invoice";

export default function POS() {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [customerId, setCustomerId] = useState("walkin");
  const [paymentType, setPaymentType] = useState("cash");
  const [discount, setDiscount] = useState(0);
  const [deposit, setDeposit] = useState(0);
  const [tendered, setTendered] = useState("");
  const [lastSale, setLastSale] = useState(null);

  useEffect(() => {
    api.get("/inventory").then((r) => setProducts(r.data.filter((i) => i.price > 0))).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const add = (p) => setCart((c) => {
    const ex = c.find((x) => x.name === p.name);
    if (ex) return c.map((x) => x.name === p.name ? { ...x, qty: x.qty + 1 } : x);
    return [...c, { name: p.name, price: p.price, qty: 1 }];
  });
  const changeQty = (name, d) => setCart((c) => c.map((x) => x.name === name ? { ...x, qty: Math.max(1, x.qty + d) } : x));
  const removeItem = (name) => setCart((c) => c.filter((x) => x.name !== name));

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const total = Math.max(subtotal - Number(discount || 0), 0) + Number(deposit || 0);
  const change = Number(tendered || 0) - total;

  const checkout = async () => {
    if (!cart.length) return toast.error("Cart is empty");
    if (paymentType === "credit" && customerId === "walkin") return toast.error("Select a customer for credit sale");
    const cust = customers.find((c) => c.id === customerId);
    try {
      const { data } = await api.post("/sales", {
        items: cart, customer_id: customerId === "walkin" ? null : customerId,
        customer_name: cust?.name || "Walk-in", payment_type: paymentType,
        discount: Number(discount || 0), deposit: Number(deposit || 0),
        amount_tendered: Number(tendered || 0),
      });
      setLastSale(data);
      toast.success(`Sale completed — ${money(data.total)}`);
      setCart([]); setDiscount(0); setDeposit(0); setTendered(""); setCustomerId("walkin"); setPaymentType("cash");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5 animate-fade-up">
      <div className="lg:col-span-2 space-y-4">
        <div>
          <h1 className="font-head text-2xl font-bold">Sales / Point of Sale</h1>
          <p className="text-muted-foreground text-sm">Quick checkout — cash, credit, card, wholesale.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {products.map((p) => (
            <button key={p.id} data-testid="pos-product" onClick={() => add(p)}
              className="border rounded-lg bg-card p-4 text-left hover:-translate-y-0.5 hover:border-primary transition-transform">
              <div className="text-sm font-medium leading-tight">{p.name}</div>
              <div className="text-primary font-head font-bold mt-2">{money(p.price)}</div>
              <div className="text-[11px] text-muted-foreground">Stock: {p.quantity} {p.unit}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="border rounded-lg bg-card p-4 h-fit lg:sticky lg:top-20 space-y-3">
        <div className="flex items-center gap-2 font-head font-semibold"><ShoppingCartIcon size={20} weight="duotone" className="text-primary" /> Cart ({cart.length})</div>
        <div className="space-y-2 max-h-64 overflow-auto">
          {cart.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">Tap a product to add</div>}
          {cart.map((i) => (
            <div key={i.name} className="flex items-center gap-2 text-sm border-b pb-2">
              <div className="flex-1"><div className="font-medium leading-tight">{i.name}</div><div className="text-[11px] text-muted-foreground">{money(i.price)} × {i.qty}</div></div>
              <button onClick={() => changeQty(i.name, -1)} className="p-1 rounded hover:bg-secondary"><MinusIcon size={14} /></button>
              <span className="w-5 text-center">{i.qty}</span>
              <button onClick={() => changeQty(i.name, 1)} className="p-1 rounded hover:bg-secondary"><PlusIcon size={14} /></button>
              <button onClick={() => removeItem(i.name)} className="p-1 rounded hover:bg-destructive/10 text-destructive"><TrashIcon size={14} /></button>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <div><Label className="text-xs text-muted-foreground mb-1 block">Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}><SelectTrigger data-testid="pos-customer"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="walkin">Walk-in</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-muted-foreground mb-1 block">Payment</Label>
              <Select value={paymentType} onValueChange={setPaymentType}><SelectTrigger data-testid="pos-payment"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="credit">Credit</SelectItem><SelectItem value="wholesale">Wholesale</SelectItem></SelectContent></Select>
            </div>
            <div><Label className="text-xs text-muted-foreground mb-1 block">Cyl. Deposit</Label><Input type="number" value={deposit} onChange={(e) => setDeposit(e.target.value)} /></div>
          </div>
          <div><Label className="text-xs text-muted-foreground mb-1 block">Discount (BZ$)</Label><Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} /></div>
        </div>

        <div className="border-t pt-3 space-y-1 text-sm">
          <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{money(subtotal)}</span></div>
          {Number(discount) > 0 && <div className="flex justify-between text-muted-foreground"><span>Discount</span><span>-{money(discount)}</span></div>}
          {Number(deposit) > 0 && <div className="flex justify-between text-muted-foreground"><span>Deposit</span><span>{money(deposit)}</span></div>}
          <div className="flex justify-between font-head font-bold text-lg pt-1"><span>Total</span><span className="text-primary">{money(total)}</span></div>
          <div className="pt-2"><Label className="text-xs text-muted-foreground mb-1 block">Cash Received (BZ$)</Label>
            <Input data-testid="pos-tendered" type="number" value={tendered} onChange={(e) => setTendered(e.target.value)} placeholder="Amount customer paid" />
          </div>
          {Number(tendered) > 0 && (
            <div className={`flex justify-between font-head font-bold text-lg ${change < 0 ? "text-destructive" : "text-emerald-600"}`} data-testid="pos-change">
              <span>{change < 0 ? "Balance Due" : "Change"}</span><span>{money(Math.abs(change))}</span>
            </div>
          )}
        </div>

        <Button className="w-full" data-testid="pos-checkout" onClick={checkout}><ReceiptIcon size={18} className="mr-1.5" /> Complete Sale</Button>

        {lastSale && (
          <div className="border rounded-md p-3 bg-emerald-500/10 text-sm space-y-2">
            <div>
              <div className="font-medium text-emerald-600">✓ {lastSale.invoice_no}</div>
              <div className="text-muted-foreground text-xs">{money(lastSale.total)} · {lastSale.payment_type}</div>
            </div>
            <Button variant="outline" size="sm" className="w-full" data-testid="pos-print-invoice" onClick={() => printInvoice(lastSale)}>
              <PrinterIcon size={16} className="mr-1.5" /> Print Invoice / Receipt
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
